<template>
  <DialogHeader>Copyright info</DialogHeader>
  <DialogContent class="overflow-y-auto">
    <div class="w-full h-[176px] p-[9px] bg-[#FAFAFA] rounded-md mb-6">
      <ThumbnailIcon
        class="w-full h-full"
        :icon-size="140"
        :type="file.type"
        :image-url="previewImageUrl"
        showShadow
      />
    </div>

    <!-- File info -->
    <div
      v-for="(info, index) in fileInformation"
      :key="index"
      class="w-full h-[30px] font-primary border-b border-[#E5E5E5] flex items-center justify-between text-[12px] leading-[16px] text-gray-800"
    >
      <div class="opacity-60">{{ info.label }}</div>
      <div class="font-secondary">{{ info.value }}</div>
    </div>

    <!-- Ownership: Timestamps -->
    <div
      class="w-full min-h-[30px] py-1.5 font-primary border-b border-[#E5E5E5] flex items-start justify-between text-[12px] leading-[16px] text-gray-800"
    >
      <div class="opacity-60">Timestamp</div>
      <div class="text-right">
        <div
          v-for="registration in displayedRegistrations"
          :key="registration.origin.id"
          class="last:mb-0 flex items-center justify-end"
        >
          <Icon :size="12" name="Dacr" class="fill-gray-800 mr-1.5" />
          <div class="font-secondary leading-[18px] text-gray-800 mr-3">
            {{ formatTimestamp(registration.timestamp) }}
          </div>
          <a
            v-if="registration.certificateUrl"
            :href="registration.certificateUrl"
            target="_blank"
            class="flex items-center gap-x-1"
          >
            <Icon name="File" :size="12" class="stroke-green-700" />
            <Icon
              name="ChevronUp"
              :size="10"
              class="stroke-green-700 rotate-90"
            />
          </a>
        </div>
        <div
          v-if="hasMoreRegistrations"
          class="flex items-center gap-x-1.5 text-[12px] leading-[18px] text-gray-500 font-medium cursor-pointer justify-end mt-1"
          @click="handleRegistrationsClick"
        >
          <div class="font-secondary">+{{ registrations.length - 3 }} more</div>
          <Icon
            name="ChevronUp"
            :size="10"
            class="stroke-gray-500 rotate-180"
          />
        </div>
      </div>
    </div>

    <!-- Ownership: Owners -->
    <div
      class="w-full min-h-[30px] py-1.5 font-primary border-b border-[#E5E5E5] flex items-start justify-between text-[12px] leading-[16px] text-gray-800"
    >
      <div class="opacity-60">{{ pluralize('Owner', owners.length) }}</div>
      <div class="max-w-[180px] text-right font-secondary">
        {{ owners.join(', ') }}
      </div>
    </div>

    <!-- Ownership: Authors -->
    <div
      class="w-full min-h-[30px] py-1.5 font-primary border-b border-[#E5E5E5] flex items-start justify-between text-[12px] leading-[16px] text-gray-800"
    >
      <div class="opacity-60">{{ pluralize('Author', authors.length) }}</div>
      <div class="max-w-[180px] text-right font-secondary">
        {{ authors.join(', ') || '- -' }}
      </div>
    </div>
  </DialogContent>
</template>

<script setup lang="ts">
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import { ThumbnailIcon, Icon } from '@dacr/ui'
import { CopyrightStatus, type File } from '@/shared/graphql/router/graphql'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { formatTimestamp } from '@/shared/utils/datetime'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { computed } from 'vue'
import pluralize from 'pluralize'

const props = defineProps<{
  file: File
}>()

const { open } = useDialog()

const previewImageUrl = computed(() =>
  props.file.asset?.previewImageUrl
    ? applyAuthorizationToken(props.file.asset.previewImageUrl, [
        { key: 'width', value: 300 },
      ])
    : undefined
)

const isInactive = computed(
  () => props.file.copyright?.status === CopyrightStatus.Inactive
)

const fileInformation = computed(() => [
  {
    label: 'Size',
    value: props.file.metadata?.size || 0,
  },
  {
    label: 'Type',
    value: props.file.type,
  },
  {
    label: 'Visibility',
    value: isInactive.value
      ? '- -'
      : props.file.settings?.isPrivate
        ? 'Private'
        : 'Public',
  },
])

const registrations = computed(() => props.file.copyright?.registrations ?? [])

const displayedRegistrations = computed(() => registrations.value.slice(0, 3))

const hasMoreRegistrations = computed(() => registrations.value.length > 3)

const owners = computed(() => {
  const ownerNames: string[] = []
  if (props.file.owner?.name) ownerNames.push(props.file.owner.name)
  props.file.coOwners?.edges?.forEach((edge) => {
    if (edge.node?.entity?.name) ownerNames.push(edge.node.entity.name)
  })
  return ownerNames
})

const authors = computed(() => {
  const authorNames: string[] = []
  props.file.authors?.edges?.forEach((edge) => {
    if (edge.node?.entity?.name) authorNames.push(edge.node.entity.name)
  })
  return authorNames
})

const handleRegistrationsClick = () => {
  open(Dialogs.Registrations, {
    stack: true,
    props: { file: props.file },
  })
}
</script>
